import React, { useEffect, useMemo, useState } from 'react';

import { mergeFiles } from 'data-providers/mainAnketaProvider';
import { getURL } from 'data-providers/common';

import OutsideClickHandler from 'react-outside-click-handler';
import { checkImagesLoaded } from 'utils/helpers';
import { CONTROLS_NAMES, IMG_FORMATS } from '../../FileUpload.constants';
import { GET_ANKETA_DOCUMENT_URL, GET_FILE_URL } from 'constants/url';
import { loadDocumentTrack } from 'metrika/documents';

import { DownloadCloudSvg, EditSvg, MinusSolidSvg } from 'assets/img';
import * as S from './FileView.styles';

export const useFileViewPresenter = ({
  documentData,
  documentTypes,
  classify,
  onDelete,
  onClose,
}) => {
  const { type, list } = documentData;

  const [page, setPage] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const [secondPage, setSecondPage] = useState(0);
  const [documentsImg, setDocumentsImg] = useState([]);
  const [documentType, setDocumentType] = useState(type);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [changeDocumentType, setChangeDocumentType] = useState(classify);

  const controls = useMemo(
    () => [
      {
        name: CONTROLS_NAMES.CHANGE_DOCUMENT_TYPE,
        icon: <EditSvg />,
        dataTest: 'FileView-ChangeTypeButton',
        hidden: documentTypes?.length === 0,
        func: () => setChangeDocumentType(true),
      },
      {
        name: CONTROLS_NAMES.DOWNLOAD,
        icon: <DownloadCloudSvg />,
        dataTest: 'FileView-DownloadButton',
        func: async () => {
          try {
            if (list.length) {
              if (list.length > 1) {
                const filePath = await mergeFiles({
                  id: list[0]?.applicationId,
                  type: documentType?.sysName,
                  format: 'pdf',
                });

                const mergedUrl = `${getURL()}${filePath.fileURL}`;

                window.open(mergedUrl, '_blank');
              } else {
                const fileUrl = GET_FILE_URL(list[0].id);

                window.open(fileUrl, '_blank');
              }
              loadDocumentTrack(documentType.sysName);
            }
          } catch (err) {
            console.error('Download error:', err);
          }
        },
      },
      {
        name: CONTROLS_NAMES.DELETE,
        icon: <MinusSolidSvg />,
        dataTest: 'FileView-DeleteButton',
        menu: (
          <OutsideClickHandler onOutsideClick={() => setShowMenu(false)}>
            <S.ControlMenu data-test={'FileView-DeleteButton-Menu'}>
              {list.length > 1 && (
                <S.MenuButton
                  onClick={() =>
                    onDelete({
                      type: 'file',
                      list: [documentsImg[page]],
                      docType: documentType.sysName,
                    })
                  }
                >
                  Удалить страницу
                </S.MenuButton>
              )}

              <S.MenuButton
                onClick={() =>
                  onDelete({
                    type: 'type',
                    list: documentsImg,
                    docType: documentType.sysName,
                  })
                }
              >
                Удалить документ
              </S.MenuButton>
            </S.ControlMenu>
          </OutsideClickHandler>
        ),
        func: () => setShowMenu(true),
      },
    ],
    [documentTypes?.length, documentType?.sysName, list, documentsImg, page, onDelete]
  );

  const getTitle = useMemo(() => {
    return documentType.value || (documentsImg[page] && documentsImg[page].type.value);
  }, [page, documentsImg]);

  const getPageNumber = useMemo(() => {
    return (
      documentsImg[page] &&
      ((documentsImg[page].classification.pages.length > 0 &&
        documentsImg[page].classification.pages) ||
        page + 1)
    );
  }, [page, documentsImg]);

  const showLeftControl = useMemo(() => {
    return page > 0;
  }, [page]);

  const showRightControl = useMemo(() => {
    return page + 1 < documentsImg.length;
  }, [page, documentsImg.length]);

  const showPDFViewer = useMemo(() => {
    return documentsImg && documentsImg[page] && 'application/pdf' === documentsImg[page].mimeType;
  }, [page, documentsImg]);

  const showCarousel = useMemo(() => {
    return documentsImg && documentsImg.length > 0;
  }, [documentsImg]);

  const showImgView = useMemo(() => {
    return documentsImg && documentsImg[page] && IMG_FORMATS.includes(documentsImg[page].mimeType);
  }, [documentsImg]);

  const showImgZoom = useMemo(() => {
    return documentsImg.length > 0 && IMG_FORMATS.includes(documentsImg[page].mimeType);
  }, [documentsImg]);

  useEffect(() => {
    if (list) {
      const getMergeFiles = async () => {
        try {
          let documentsWithImg = [];

          if (list.length) {
            documentsWithImg = list.map((document) => ({
              ...document,
              imgUrl: GET_ANKETA_DOCUMENT_URL(document),
              mergedUrl: '',
            }));
          }

          setDocumentsImg(documentsWithImg);

          await checkImagesLoaded(documentsWithImg.map((document) => document.imgUrl));

          setTimeout(() => {
            setImagesLoaded(true);
          }, 500);
        } catch (err) {
          console.error('Error loading documents:', err);
          onClose(null);
        }
      };

      getMergeFiles();
    } else {
      onClose(null);
    }
  }, [list]);

  return {
    page,
    showMenu,
    classify,
    controls,
    secondPage,
    imagesLoaded,
    documentsImg,
    documentType,
    documentTypes,
    getTitle,
    getPageNumber,
    showCarousel,
    showPDFViewer,
    showImgView,
    showImgZoom,
    showLeftControl,
    showRightControl,
    changeDocumentType,
    setPage,
    setSecondPage,
    setDocumentType,
    setChangeDocumentType,
  };
};
